/**
 * Deposit Service — เงินมัดจำรายการเช่า (Deposit v2 Phase 1)
 *
 * endpoint ทั้งหมดอยู่ใต้ /rentals/{rental_id}/items/{item_id}/deposit/* (permission rentals.deposit_manage)
 * ยกเว้น GET /rentals/{rental_id}/deposit-summary (rentals.view)
 *
 * ค่าที่คำนวณจากวันทำการ (due date / business days / overdue) มาจาก backend เท่านั้น
 * — ห้ามคำนวณวันทำการฝั่ง client เพราะ SLA/วันหยุดอ่านจาก system_settings
 */
import ApiWithLogging from '../utils/apiWithLogging';

// ========== Enum / constants ==========

export type DepositStatus = 'PENDING' | 'PAID' | 'RETURNED' | 'WAIVED' | 'PENDING_REFUND';

export type DepositRefundMethod = 'CASH' | 'BANK_TRANSFER' | 'CHECK' | 'OTHER';

export const DEPOSIT_REFUND_METHOD_LABELS: Record<DepositRefundMethod, string> = {
  BANK_TRANSFER: 'โอนเงิน',
  CASH: 'เงินสด',
  CHECK: 'เช็ค',
  OTHER: 'อื่นๆ',
};

export const DEPOSIT_REFUND_METHOD_OPTIONS: Array<{ value: DepositRefundMethod; label: string }> = [
  { value: 'BANK_TRANSFER', label: DEPOSIT_REFUND_METHOD_LABELS.BANK_TRANSFER },
  { value: 'CASH', label: DEPOSIT_REFUND_METHOD_LABELS.CASH },
  { value: 'CHECK', label: DEPOSIT_REFUND_METHOD_LABELS.CHECK },
  { value: 'OTHER', label: DEPOSIT_REFUND_METHOD_LABELS.OTHER },
];

/** SLA คืนมัดจำ (วันทำการ) — ค่า default ตรงกับ backend fallback; ใช้แสดงข้อความเท่านั้น */
export const DEPOSIT_REFUND_SLA_BUSINESS_DAYS = 15;

// ========== Types ==========

/**
 * ฟิลด์มัดจำของ rental_item (ทั้งที่เก็บใน DB + computed จาก backend)
 * ใช้ extend ใน RentalItem ของทุกหน้าที่แสดงมัดจำ
 */
export interface RentalItemDepositFields {
  deposit_amount?: number | null;
  deposit_waived?: boolean;
  deposit_status?: DepositStatus | string | null;
  deposit_paid_date?: string | null;
  deposit_returned_date?: string | null;
  // Deposit v2
  deposit_hold?: boolean;
  deposit_hold_note?: string | null;
  deposit_hold_at?: string | null;
  deposit_refund_pending_since?: string | null;
  deposit_returned_by_user_id?: number | null;
  deposit_refund_method?: DepositRefundMethod | string | null;
  deposit_refund_reference?: string | null;
  deposit_refund_note?: string | null;
  // computed (backend only)
  deposit_refund_due_date?: string | null;
  deposit_refund_business_days_elapsed?: number | null;
  deposit_refund_overdue?: boolean;
  deposit_returned_by_name?: string | null;
}

export interface DepositSummaryBucket {
  count: number;
  amount: number;
}

export interface DepositSummary {
  total: number;
  pending_refund: DepositSummaryBucket;
  held: DepositSummaryBucket;
  returned: DepositSummaryBucket;
  paid: DepositSummaryBucket;
  overdue_count: number;
}

/** FB-2026-0018: ใบแจ้งหนี้รายการเดียว (มัดจำ) ที่ชำระครบแล้วของสัญญานี้ — GET /rentals/{id}/deposit-invoices */
export interface RentalDepositInvoice {
  invoice_id: number;
  invoice_number: string;
  source_quotation_id?: number | null;
  source_quotation_number?: string | null;
  total_amount: number | string;
  /** Σ แถวมัดจำ ก่อน VAT — เทียบกับมัดจำในสัญญา */
  deposit_subtotal: number | string;
  /** วันที่ชำระ = paid_date ที่จะบันทึก */
  paid_date?: string | null;
  /** รายการที่ยังรอชำระมัดจำ — ว่าง = ไม่ต้องกดปุ่ม */
  pending_rental_item_ids: number[];
  pending_deposit_total: number | string;
  recorded_rental_item_ids: number[];
}

export interface DepositFromInvoiceResponse {
  invoice_id: number;
  invoice_number: string;
  paid_date: string;
  rental_item_ids: number[];
  linked_rental_item_ids: number[];
  rental_deposit_summary: DepositSummary;
  warnings: string[];
}

/** RentalItem ที่ backend ส่งกลับหลัง action — เก็บแบบหลวมเพราะแต่ละหน้ามี shape ต่างกัน */
export type DepositActionItem = RentalItemDepositFields & {
  id: number;
  [key: string]: unknown;
};

export interface DepositActionResponse {
  item: DepositActionItem;
  rental_deposit_summary: DepositSummary;
  warnings: string[];
}

export interface DepositReturnRequest {
  returned_date: string;            // YYYY-MM-DD
  refund_method?: DepositRefundMethod;
  refund_reference?: string;        // ≤100
  note?: string;                    // ≤500
  release_hold?: boolean;
}

export interface DepositHoldRequest {
  note?: string;
}

export interface DepositUnholdRequest {
  note?: string;
}

export interface DepositReopenRequest {
  reason: string;
}

export interface DepositMarkPaidRequest {
  paid_date: string;                // YYYY-MM-DD
}

// ========== Helpers ==========

/** แปลง 'YYYY-MM-DD' (หรือ ISO datetime) → 'dd/mm/yyyy' โดยไม่ผ่าน Date (กัน timezone เลื่อนวัน) */
export const formatDateDMY = (value: string | null | undefined): string => {
  if (!value) return '-';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(value);
  return `${m[3]}/${m[2]}/${m[1]}`;
};

export const getDepositRefundMethodLabel = (method: string | null | undefined): string => {
  if (!method) return '';
  return DEPOSIT_REFUND_METHOD_LABELS[method as DepositRefundMethod] ?? method;
};

/** rental อยู่สถานะปลายทาง (ยกเลิก/จบ) — มัดจำ PAID ในสถานะนี้ต้องคืนได้ */
export const isRentalTerminal = (status: string | null | undefined): boolean =>
  status === 'COMPLETED' || status === 'CANCELLED';

/** item นี้กด "บันทึกการคืนมัดจำ" ได้ (ยังต้องมี rentals.deposit_manage) */
export const canReturnDeposit = (
  item: Pick<RentalItemDepositFields, 'deposit_status'>,
  rentalStatus: string | null | undefined,
): boolean =>
  item.deposit_status === 'PENDING_REFUND' ||
  (item.deposit_status === 'PAID' && isRentalTerminal(rentalStatus));

/** item นี้กด "บันทึกรับมัดจำ" ได้ (PENDING บน rental ที่ยังไม่จบ + มียอด) */
export const canMarkDepositPaid = (
  item: Pick<RentalItemDepositFields, 'deposit_status' | 'deposit_amount'>,
  rentalStatus: string | null | undefined,
): boolean =>
  (item.deposit_status === 'PENDING' || !item.deposit_status) &&
  (rentalStatus === 'DRAFT' || rentalStatus === 'ACTIVE') &&
  Number(item.deposit_amount || 0) > 0;

// ========== Service ==========

const base = (rentalId: number, itemId: number) =>
  `/rentals/${rentalId}/items/${itemId}/deposit`;

const depositService = {
  /** บันทึกคืนมัดจำ (PENDING_REFUND | PAID → RETURNED) */
  async returnDeposit(rentalId: number, itemId: number, data: DepositReturnRequest): Promise<DepositActionResponse> {
    return ApiWithLogging.post<DepositActionResponse>(`${base(rentalId, itemId)}/return`, data, {}, {
      service: 'depositService',
      action: 'deposit_return',
      entityType: 'rental',
      entityId: rentalId,
      details: { item_id: itemId, returned_date: data.returned_date, refund_method: data.refund_method, release_hold: data.release_hold },
    });
  },

  /** คงมัดจำไว้ก่อน (hold=true) */
  async holdDeposit(rentalId: number, itemId: number, data: DepositHoldRequest = {}): Promise<DepositActionResponse> {
    return ApiWithLogging.post<DepositActionResponse>(`${base(rentalId, itemId)}/hold`, data, {}, {
      service: 'depositService',
      action: 'deposit_hold',
      entityType: 'rental',
      entityId: rentalId,
      details: { item_id: itemId },
    });
  },

  /** ปลดการคงมัดจำ (hold=false, นับวันทำการใหม่) */
  async unholdDeposit(rentalId: number, itemId: number, data: DepositUnholdRequest = {}): Promise<DepositActionResponse> {
    return ApiWithLogging.post<DepositActionResponse>(`${base(rentalId, itemId)}/unhold`, data, {}, {
      service: 'depositService',
      action: 'deposit_unhold',
      entityType: 'rental',
      entityId: rentalId,
      details: { item_id: itemId },
    });
  },

  /** แก้ไขบันทึกคืน (RETURNED → PENDING_REFUND, ล้างข้อมูลคืน) */
  async reopenDeposit(rentalId: number, itemId: number, data: DepositReopenRequest): Promise<DepositActionResponse> {
    return ApiWithLogging.post<DepositActionResponse>(`${base(rentalId, itemId)}/reopen`, data, {}, {
      service: 'depositService',
      action: 'deposit_reopen',
      entityType: 'rental',
      entityId: rentalId,
      details: { item_id: itemId },
    });
  },

  /** บันทึกรับมัดจำ (PENDING → PAID) */
  async markDepositPaid(rentalId: number, itemId: number, data: DepositMarkPaidRequest): Promise<DepositActionResponse> {
    return ApiWithLogging.post<DepositActionResponse>(`${base(rentalId, itemId)}/mark-paid`, data, {}, {
      service: 'depositService',
      action: 'deposit_mark_paid',
      entityType: 'rental',
      entityId: rentalId,
      details: { item_id: itemId, paid_date: data.paid_date },
    });
  },

  /** FB-2026-0018: ใบมัดจำ (ONE_OFF) ที่ชำระแล้วของสัญญา — หาผ่านใบงาน/ใบเสนอราคา · หาไม่เจอ = [] (rentals.view) */
  async getDepositInvoices(rentalId: number): Promise<RentalDepositInvoice[]> {
    const res = await ApiWithLogging.get<{ invoices: RentalDepositInvoice[] }>(`/rentals/${rentalId}/deposit-invoices`, {}, {
      service: 'depositService',
      action: 'view_deposit_invoices',
      entityType: 'rental',
      entityId: rentalId,
    });
    return res?.invoices || [];
  },

  /** FB-2026-0018: "บันทึกรับมัดจำจากใบนี้" — PENDING → PAID ทุกรายการที่รอ · paid_date = วันชำระ (rentals.deposit_manage) */
  async markPaidFromInvoice(rentalId: number, invoiceId: number): Promise<DepositFromInvoiceResponse> {
    return ApiWithLogging.post<DepositFromInvoiceResponse>(`/rentals/${rentalId}/deposit/mark-paid-from-invoice`, { invoice_id: invoiceId }, {}, {
      service: 'depositService',
      action: 'deposit_mark_paid_from_invoice',
      entityType: 'rental',
      entityId: rentalId,
      details: { invoice_id: invoiceId },
    });
  },

  /** สรุปมัดจำของ rental (rentals.view) */
  async getDepositSummary(rentalId: number): Promise<DepositSummary> {
    return ApiWithLogging.get<DepositSummary>(`/rentals/${rentalId}/deposit-summary`, {}, {
      service: 'depositService',
      action: 'view_deposit_summary',
      entityType: 'rental',
      entityId: rentalId,
    });
  },
};

export default depositService;
